import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  Divider,
  IconButton,
  Stack,
  Paper,
  ImageList,
  ImageListItem,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Pets as PetsIcon,
  CalendarToday as CalendarIcon,
  LocalHospital as HospitalIcon,
  Assignment as AssignmentIcon,
  Person as PersonIcon,
  Scale as ScaleIcon,
  ColorLens as ColorIcon,
  Description as DescriptionIcon,
  PhotoLibrary as PhotoIcon
} from '@mui/icons-material';
import { ShelterAnimal, AnimalPhoto } from '@services/sheltersService';
import { shelterService } from '@services/sheltersService';

interface AnimalViewModalProps {
  open: boolean;
  onClose: () => void;
  animalId: string | null;
  onEdit?: (animalId: string) => void;
}

const AnimalViewModal: React.FC<AnimalViewModalProps> = ({
  open,
  onClose,
  animalId,
  onEdit
}) => {
  const [animal, setAnimal] = useState<ShelterAnimal | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && animalId) {
      loadAnimalDetails();
    }
  }, [open, animalId]);

  const loadAnimalDetails = async () => {
    if (!animalId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await shelterService.getAnimalById(animalId);
      setAnimal(response.data || response);
    } catch (err) {
      console.error('Error loading animal details:', err);
      setError('Eroare la încărcarea detaliilor animalului');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return 'success';
      case 'reserved': return 'warning';
      case 'adopted': return 'info';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'available': return 'Disponibil';
      case 'reserved': return 'Rezervat';
      case 'adopted': return 'Adoptat';
      default: return status;
    }
  };

  const getGenderLabel = (gender: string) => {
    return gender === 'male' ? 'Mascul' : gender === 'female' ? 'Femelă' : gender;
    // Pentru compatibilitate cu valorile vechi
    if (gender === 'M') return 'Mascul';
    if (gender === 'F') return 'Femelă';
    return gender;
  };

  const getSpeciesLabel = (species: string) => {
    switch (species) {
      case 'dog': return 'Câine';
      case 'cat': return 'Pisică';
      case 'bird': return 'Pasăre';
      case 'rabbit': return 'Iepure';
      case 'horse': return 'Cal';
      case 'other': return 'Altul';
      // Pentru compatibilitate cu valorile vechi
      case 'caine': return 'Câine';
      case 'pisica': return 'Pisică';
      case 'altul': return 'Altul';
      default: return species;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ro-RO');
  };

  const calculateAge = (ageMonths: number) => {
    const years = Math.floor(ageMonths / 12);
    const months = ageMonths % 12;
    
    if (years === 0) {
      return `${months} luni`;
    } else if (months === 0) {
      return `${years} ${years === 1 ? 'an' : 'ani'}`;
    } else {
      return `${years} ${years === 1 ? 'an' : 'ani'} și ${months} luni`;
    }
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (error) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Alert severity="error">{error}</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Închide</Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (!animal) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Alert severity="info">Animalul nu a fost găsit.</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Închide</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ bgcolor: 'primary.main' }}>
              <PetsIcon />
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight="bold">
                {animal.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {getSpeciesLabel(animal.species)} • {animal.breed || 'Fără rasă specificată'}
              </Typography>
            </Box>
          </Box>
          <Box display="flex" gap={1}>
            {onEdit && (
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => onEdit(animal.id)}
              >
                Editează
              </Button>
            )}
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
          {/* Coloana stângă - Informații generale */}
          <Box sx={{ flex: 1 }}>
            {/* Status și informații de bază */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Status și Informații Generale
                </Typography>
                <Stack spacing={2}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      Status:
                    </Typography>
                    <Chip 
                      label={getStatusLabel(animal.status)} 
                      color={getStatusColor(animal.status) as any}
                      size="small"
                    />
                  </Box>
                  
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      Vârstă:
                    </Typography>
                    <Typography variant="body2">
                      {animal.age_months ? calculateAge(animal.age_months) : 'Necunoscută'}
                    </Typography>
                  </Box>

                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      Sex:
                    </Typography>
                    <Typography variant="body2">
                      {getGenderLabel(animal.gender || (animal as any).sex)}
                    </Typography>
                  </Box>

                  {animal.color && (
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        Culoare:
                      </Typography>
                      <Typography variant="body2">
                        {animal.color}
                      </Typography>
                    </Box>
                  )}

                  {animal.weight_kg && (
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        Greutate:
                      </Typography>
                      <Typography variant="body2">
                        {animal.weight_kg} kg
                      </Typography>
                    </Box>
                  )}

                  {animal.size && (
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        Talie:
                      </Typography>
                      <Typography variant="body2">
                        {animal.size === 'mica' ? 'Mică' : 
                         animal.size === 'medie' ? 'Medie' : 'Mare'}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>

            {/* Date de identificare */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Date de Identificare
                </Typography>
                <Stack spacing={2}>
                  {animal.chip_number && (
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        Microcip:
                      </Typography>
                      <Typography variant="body2">
                        {animal.chip_number}
                      </Typography>
                    </Box>
                  )}

                  {animal.passport_number && (
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        Pasaport:
                      </Typography>
                      <Typography variant="body2">
                        {animal.passport_number}
                      </Typography>
                    </Box>
                  )}

                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      Microcip:
                    </Typography>
                    <Chip 
                      label={animal.microchipped ? 'Da' : 'Nu'} 
                      color={animal.microchipped ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            {/* Stare de sănătate */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Stare de Sănătate
                </Typography>
                <Stack spacing={2}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      Vaccinat:
                    </Typography>
                    <Chip 
                      label={animal.vaccinated ? 'Da' : 'Nu'} 
                      color={animal.vaccinated ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>

                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      Sterilizat:
                    </Typography>
                    <Chip 
                      label={animal.sterilized ? 'Da' : 'Nu'} 
                      color={animal.sterilized ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>

                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      Deparazitat:
                    </Typography>
                    <Chip 
                      label={animal.dewormed ? 'Da' : 'Nu'} 
                      color={animal.dewormed ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>

                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      Deparazitare externă:
                    </Typography>
                    <Chip 
                      label={animal.deparasit_extern ? 'Da' : 'Nu'} 
                      color={animal.deparasit_extern ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>

                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      Vaccinat împotriva rabiei:
                    </Typography>
                    <Chip 
                      label={animal.vaccinated_rabies ? 'Da' : 'Nu'} 
                      color={animal.vaccinated_rabies ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Box>

          {/* Coloana dreaptă - Descriere și poze */}
          <Box sx={{ flex: 1 }}>
            {/* Descriere */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Descriere
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {animal.description || 'Nu există descriere disponibilă.'}
                </Typography>
              </CardContent>
            </Card>

            {/* Nevoi speciale */}
            {animal.special_needs && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Nevoi Speciale
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {animal.special_needs}
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/* Galerie foto */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Galerie Foto
                </Typography>
                {animal.photos && animal.photos.length > 0 ? (
                  <ImageList cols={2} rowHeight={150}>
                    {animal.photos.map((photo: AnimalPhoto) => (
                      <ImageListItem key={photo.id}>
                        <img
                          src={(() => {
                            if (photo.file_path.startsWith('http')) return photo.file_path;
                            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1';
                            const baseURL = API_URL.replace('/api/v1', '');
                            return `${baseURL}${photo.file_path}`;
                          })()}
                          alt={photo.caption || animal.name}
                          loading="lazy"
                          style={{ borderRadius: 8 }}
                        />
                      </ImageListItem>
                    ))}
                  </ImageList>
                ) : (
                  <Box 
                    display="flex" 
                    flexDirection="column" 
                    alignItems="center" 
                    justifyContent="center" 
                    py={4}
                    color="text.secondary"
                  >
                    <PhotoIcon sx={{ fontSize: 48, mb: 1 }} />
                    <Typography variant="body2">
                      Nu există poze disponibile
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Informații administrative */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Informații Administrative
                </Typography>
                <Stack spacing={2}>
                  {animal.intake_date && (
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        Data intrării:
                      </Typography>
                      <Typography variant="body2">
                        {formatDate(animal.intake_date)}
                      </Typography>
                    </Box>
                  )}

                  {animal.adoption_date && (
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        Data adoptiei:
                      </Typography>
                      <Typography variant="body2">
                        {formatDate(animal.adoption_date)}
                      </Typography>
                    </Box>
                  )}

                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      Adoptabil:
                    </Typography>
                    <Chip 
                      label={animal.adoptable ? 'Da' : 'Nu'} 
                      color={animal.adoptable ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>

                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      Creat la:
                    </Typography>
                    <Typography variant="body2">
                      {formatDate(animal.created_at)}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Închide
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AnimalViewModal;
